
import React, { useState, useEffect } from 'react'
import {X, User, Mail, Phone, CreditCard, Building, Briefcase, Calendar, Shield, FileText, Settings, Eye, Plus, Edit, Trash2, Lock} from 'lucide-react'

interface Usuario {
  _id?: string
  nome: string
  email: string
  telefone?: string
  cpf?: string
  senha: string
  perfil: 'administrador' | 'juridico' | 'financeiro' | 'compras' | 'engenheiro' | 'gestor'
  status: 'ativo' | 'inativo' | 'bloqueado'
  departamento?: string
  cargo?: string
  dataAdmissao?: string
  permissoes: string[]
  permissoesDetalhadas: {
    [modulo: string]: {
      visualizar: boolean
      adicionar: boolean
      editar: boolean
      excluir: boolean
    }
  }
  observacoes?: string
  creator: string
}

interface UsuarioFormProps {
  usuario?: Usuario
  onSubmit: (usuario: Omit<Usuario, '_id'>) => void
  onCancel: () => void
  getPermissoesPorPerfil: (perfil: Usuario['perfil']) => string[]
  getPermissoesDetalhadasPorPerfil: (perfil: Usuario['perfil']) => any
  getModulosDisponiveis: () => Array<{ key: string; label: string }>
}

const UsuarioForm: React.FC<UsuarioFormProps> = ({
  usuario,
  onSubmit,
  onCancel,
  getPermissoesPorPerfil,
  getPermissoesDetalhadasPorPerfil,
  getModulosDisponiveis
}) => {
  const [formData, setFormData] = useState<Omit<Usuario, '_id'>>({
    nome: '',
    email: '',
    telefone: '',
    cpf: '',
    senha: '',
    perfil: 'engenheiro',
    status: 'ativo',
    departamento: '',
    cargo: '',
    dataAdmissao: new Date().toISOString().split('T')[0],
    permissoes: [],
    permissoesDetalhadas: {},
    observacoes: '',
    creator: 'sistema'
  })

  const [permissoesDisponiveis, setPermissoesDisponiveis] = useState<string[]>([])
  const [activeTab, setActiveTab] = useState<'basicas' | 'permissoes'>('basicas')
  const [showPassword, setShowPassword] = useState(false)

  const perfilOptions = [
    { value: 'administrador', label: 'Administrador', description: 'Acesso total ao sistema' },
    { value: 'juridico', label: 'Jurídico', description: 'Gestão de contratos e documentos legais' },
    { value: 'financeiro', label: 'Financeiro', description: 'Controle financeiro e fluxo de caixa' },
    { value: 'compras', label: 'Compras', description: 'Gestão de compras e fornecedores' },
    { value: 'engenheiro', label: 'Engenheiro', description: 'Planejamento e execução de obras' },
    { value: 'gestor', label: 'Gestor', description: 'Gestão de projetos e equipes' }
  ]

  const statusOptions = [
    { value: 'ativo', label: 'Ativo', color: 'text-green-600' },
    { value: 'inativo', label: 'Inativo', color: 'text-gray-600' },
    { value: 'bloqueado', label: 'Bloqueado', color: 'text-red-600' }
  ]

  useEffect(() => {
    if (usuario) {
      setFormData({
        nome: usuario.nome,
        email: usuario.email,
        telefone: usuario.telefone || '',
        cpf: usuario.cpf || '',
        senha: '', // Sempre vazio para edição por segurança
        perfil: usuario.perfil,
        status: usuario.status,
        departamento: usuario.departamento || '',
        cargo: usuario.cargo || '',
        dataAdmissao: usuario.dataAdmissao ? usuario.dataAdmissao.split('T')[0] : new Date().toISOString().split('T')[0],
        permissoes: usuario.permissoes || [],
        permissoesDetalhadas: usuario.permissoesDetalhadas || {},
        observacoes: usuario.observacoes || '',
        creator: usuario.creator
      })
    }
  }, [usuario])

  useEffect(() => {
    const permissoes = getPermissoesPorPerfil(formData.perfil)
    const permissoesDetalhadas = getPermissoesDetalhadasPorPerfil(formData.perfil)
    
    setPermissoesDisponiveis(permissoes)
    setFormData(prev => ({ 
      ...prev, 
      permissoes,
      permissoesDetalhadas: { ...prev.permissoesDetalhadas, ...permissoesDetalhadas }
    }))
  }, [formData.perfil, getPermissoesPorPerfil, getPermissoesDetalhadasPorPerfil])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validações básicas
    if (!formData.nome.trim()) {
      alert('Nome é obrigatório')
      return
    }
    
    if (!formData.email.trim()) {
      alert('Email é obrigatório')
      return
    }

    if (!formData.email.includes('@')) {
      alert('Email inválido')
      return
    }

    // Validação de senha apenas para novos usuários ou quando senha foi preenchida
    if (!usuario && !formData.senha.trim()) {
      alert('Senha é obrigatória para novos usuários')
      return
    }

    if (formData.senha && formData.senha.length < 6) {
      alert('Senha deve ter pelo menos 6 caracteres')
      return
    }

    const dataToSubmit = {
      ...formData,
      dataAdmissao: formData.dataAdmissao ? new Date(formData.dataAdmissao).toISOString() : new Date().toISOString()
    }

    // Se estiver editando e senha estiver vazia, não enviar senha
    if (usuario && !formData.senha.trim()) {
      const { senha, ...dataWithoutPassword } = dataToSubmit
      onSubmit(dataWithoutPassword as any)
    } else {
      onSubmit(dataToSubmit)
    }
  }

  const handleChange = (field: keyof typeof formData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handlePermissaoChange = (modulo: string, acao: string, valor: boolean) => {
    setFormData(prev => ({
      ...prev,
      permissoesDetalhadas: {
        ...prev.permissoesDetalhadas,
        [modulo]: {
          ...prev.permissoesDetalhadas[modulo],
          [acao]: valor
        }
      }
    }))
  }

  const formatCPF = (value: string) => {
    const numbers = value.replace(/\D/g, '')
    return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
  }

  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, '')
    return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
  }

  const getPermissionIcon = (acao: string) => {
    const icons = {
      visualizar: <Eye className="w-4 h-4" />,
      adicionar: <Plus className="w-4 h-4" />,
      editar: <Edit className="w-4 h-4" />,
      excluir: <Trash2 className="w-4 h-4" />
    }
    return icons[acao as keyof typeof icons]
  }

  const getPermissionColor = (acao: string) => {
    const colors = {
      visualizar: 'text-blue-600',
      adicionar: 'text-green-600',
      editar: 'text-orange-600',
      excluir: 'text-red-600'
    }
    return colors[acao as keyof typeof colors]
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto m-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {usuario ? 'Editar Usuário' : 'Novo Usuário'}
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('basicas')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'basicas'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <User className="w-5 h-5 inline mr-2" />
              Informações Básicas
            </button>
            <button
              onClick={() => setActiveTab('permissoes')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'permissoes'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Settings className="w-5 h-5 inline mr-2" />
              Permissões Detalhadas
            </button>
          </nav>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {activeTab === 'basicas' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Informações Básicas */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <User className="w-5 h-5 mr-2 text-blue-500" />
                  Informações Básicas
                </h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome Completo *
                  </label>
                  <input
                    type="text"
                    value={formData.nome}
                    onChange={(e) => handleChange('nome', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Digite o nome completo"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="usuario@empresa.com"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Senha {!usuario && '*'}
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.senha}
                      onChange={(e) => handleChange('senha', e.target.value)}
                      className="w-full pl-10 pr-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={usuario ? "Deixe vazio para manter a senha atual" : "Digite a senha"}
                      required={!usuario}
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                    >
                      <Eye className="w-5 h-5" />
                    </button>
                  </div>
                  {!usuario && (
                    <p className="text-xs text-gray-500 mt-1">Mínimo de 6 caracteres</p>
                  )}
                  {usuario && (
                    <p className="text-xs text-gray-500 mt-1">Deixe vazio para manter a senha atual</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefone
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={formData.telefone}
                      onChange={(e) => handleChange('telefone', formatPhone(e.target.value))}
                      className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="(11) 99999-9999"
                      maxLength={15}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CPF
                  </label>
                  <div className="relative">
                    <CreditCard className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={formData.cpf}
                      onChange={(e) => handleChange('cpf', formatCPF(e.target.value))}
                      className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="123.456.789-01"
                      maxLength={14}
                    />
                  </div>
                </div>
              </div>

              {/* Informações Profissionais */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <Briefcase className="w-5 h-5 mr-2 text-blue-500" />
                  Informações Profissionais
                </h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Perfil de Acesso *
                  </label>
                  <div className="relative">
                    <Shield className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <select
                      value={formData.perfil}
                      onChange={(e) => handleChange('perfil', e.target.value)}
                      className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      {perfilOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label} - {option.description}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status *
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => handleChange('status', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    {statusOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Departamento
                  </label>
                  <div className="relative">
                    <Building className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={formData.departamento}
                      onChange={(e) => handleChange('departamento', e.target.value)}
                      className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Ex: Engenharia, Financeiro, etc."
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cargo
                  </label>
                  <input
                    type="text"
                    value={formData.cargo}
                    onChange={(e) => handleChange('cargo', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ex: Engenheiro Civil, Analista Financeiro, etc."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data de Admissão
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <input
                      type="date"
                      value={formData.dataAdmissao}
                      onChange={(e) => handleChange('dataAdmissao', e.target.value)}
                      className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* Observações */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Observações
                  </label>
                  <div className="relative">
                    <FileText className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <textarea
                      value={formData.observacoes}
                      onChange={(e) => handleChange('observacoes', e.target.value)}
                      className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      rows={3}
                      placeholder="Observações adicionais sobre o usuário..."
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'permissoes' && (
            <div className="space-y-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 flex items-center mb-2">
                  <Settings className="w-5 h-5 mr-2 text-blue-500" />
                  Configuração Detalhada de Permissões
                </h3>
                <p className="text-sm text-gray-600">
                  Configure as permissões específicas para cada módulo do sistema. 
                  As permissões padrão são definidas pelo perfil, mas podem ser personalizadas.
                </p>
              </div>

              <div className="space-y-4">
                {getModulosDisponiveis().map((modulo) => (
                  <div key={modulo.key} className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">{modulo.label}</h4>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {['visualizar', 'adicionar', 'editar', 'excluir'].map((acao) => (
                        <label key={acao} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.permissoesDetalhadas[modulo.key]?.[acao] || false}
                            onChange={(e) => handlePermissaoChange(modulo.key, acao, e.target.checked)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className={`flex items-center space-x-1 text-sm ${getPermissionColor(acao)}`}>
                            {getPermissionIcon(acao)}
                            <span className="capitalize">{acao}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Resumo das Permissões */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-3">Resumo das Permissões Básicas</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {permissoesDisponiveis.map((permissao) => (
                    <div key={permissao} className="flex items-center">
                      <Shield className="w-4 h-4 text-green-500 mr-2" />
                      <span className="text-sm text-gray-700">
                        {permissao.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {usuario ? 'Atualizar' : 'Criar'} Usuário
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default UsuarioForm
